import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle, CopyX, Loader2 } from "lucide-react";
import type { BulkUploadPlan } from "../bulk-upload-plan";

interface BulkUploadPlanSummaryProps {
  plan: BulkUploadPlan | null;
  isPlanning: boolean;
  planError: string | null;
  /** Unit creation is a PMC-only permission; vendor uploads just skip missing-unit rows. */
  allowCreateUnits: boolean;
  createMissingUnits: boolean;
  onCreateMissingUnitsChange: (checked: boolean) => void;
}

export const MAX_LIST_ITEMS = 8;

export function CappedList({ items }: { items: string[] }) {
  const visible = items.slice(0, MAX_LIST_ITEMS);
  const hiddenCount = items.length - visible.length;

  return (
    <ul className="list-disc list-inside text-sm space-y-1">
      {visible.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
      {hiddenCount > 0 && <li>…and {hiddenCount} more</li>}
    </ul>
  );
}

export default function BulkUploadPlanSummary({
  plan,
  isPlanning,
  planError,
  allowCreateUnits,
  createMissingUnits,
  onCreateMissingUnitsChange,
}: BulkUploadPlanSummaryProps) {
  if (isPlanning) {
    return (
      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Checking rows against your properties, units, and services…</span>
      </div>
    );
  }

  if (planError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Could not validate the file: {planError}</AlertDescription>
      </Alert>
    );
  }

  if (!plan) return null;

  const missingUnitLabels = [
    ...new Set(plan.missingUnits.map((row) => `${row.propertyName} — unit ${row.unitNumber}`)),
  ];

  return (
    <div className="space-y-2">
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          {plan.ready.length} appointment{plan.ready.length !== 1 ? "s" : ""} ready to import.
        </AlertDescription>
      </Alert>

      {plan.duplicates.length > 0 && (
        <Alert className="border-blue-200 bg-blue-50">
          <CopyX className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <div className="space-y-1">
              <div>
                {plan.duplicates.length} duplicate{plan.duplicates.length !== 1 ? "s" : ""} will be skipped (appointment
                already exists):
              </div>
              <CappedList items={plan.duplicates} />
            </div>
          </AlertDescription>
        </Alert>
      )}

      {plan.missingUnits.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <div className="space-y-2">
              <div>
                {plan.missingUnits.length} row{plan.missingUnits.length !== 1 ? "s" : ""} reference
                {plan.missingUnits.length === 1 ? "s" : ""} {missingUnitLabels.length} unit
                {missingUnitLabels.length !== 1 ? "s" : ""} that do{missingUnitLabels.length === 1 ? "es" : ""} not
                exist:
              </div>
              <CappedList items={missingUnitLabels} />
              {allowCreateUnits ? (
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox
                    id="create-missing-units"
                    checked={createMissingUnits}
                    onCheckedChange={(checked) => onCreateMissingUnitsChange(checked === true)}
                  />
                  <Label htmlFor="create-missing-units" className="text-amber-800 font-normal cursor-pointer">
                    Create these units automatically (bed/bath taken from service names like &ldquo;2x2&rdquo;, or 0
                    bed / 0 bath — edit later). Otherwise these rows are skipped.
                  </Label>
                </div>
              ) : (
                <div>These rows will be skipped. Ask the property manager to add the units first.</div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {plan.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              <div>
                {plan.errors.length} row{plan.errors.length !== 1 ? "s" : ""} cannot be imported:
              </div>
              <CappedList items={plan.errors} />
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
